import imageCompression from 'browser-image-compression';
import { uploadFileToAPI, settleUpload } from './observables';
import { db, handleDexieError } from "./db";
import { useUploadResultsStore } from 'stores/useUploadResultsStore';

/**
 * Uploads a pending file from IndexedDB to the API.
 * @param {string} id - The ID of the file to be uploaded.
 * @returns {Promise<void>} - A promise that resolves when the upload is complete.
 */
export const uploadPendingFileFromIndexedDB = async (id) => {
  let upload;
  let compressedBlobAsFile;
  try {
    upload = await db.imageUpload
      .where({ id: id, status: 'pending' })
      .first()
      .catch((e) => {
        return handleDexieError(e, { from: "uploadPendingFileFromIndexedDB" });
      });

    if (!upload) return;

    compressedBlobAsFile = upload.compressedFile
      ? new File([upload.compressedFile], upload.file.name, { type: upload.file.type })
      : await compressImage(upload.file);
  } catch (error) {
    console.error('Error uploading pending file from IndexedDB', error);
    // uploadFileToAPI records a tracked upload's result, but it isn't reached,
    // so record the failure here or the caller waits for a result forever.
    if (upload?.resultKey) {
      useUploadResultsStore
        .getState()
        .setResult(upload.resultKey, { status: 'failed' });
    }
    // Still counts towards its batch, or the batch would never finish.
    if (upload) settleUpload(upload);
    return;
  }

  try {
    await uploadFileToAPI(upload, compressedBlobAsFile);
  } catch (error) {
    // Still counts towards its batch, or the batch would never finish.
    if (upload) settleUpload(upload);
    console.error('Error uploading pending file from IndexedDB', error);
  }
};

export const uploadRecentPhotoFromIndexedDB = async (id, prismItem) => {
  try {
    const upload = await db.imageUpload
      .where({ id: id })
      .first()
      .catch((e) => {
        return handleDexieError(e, { from: "uploadRecentPhotoFromIndexedDB" });
      });
    if (!upload) return;

    const compressedBlobAsFile = upload.compressedFile
      ? new File([upload.compressedFile], upload.file.name, { type: upload.file.type })
      : await compressImage(upload.file);

    await uploadFileToAPI(upload, compressedBlobAsFile, prismItem);
  } catch (error) {
    console.error('Error uploading pending file from IndexedDB', error);
  }
};

/**
 * Compresses an image file using the specified compression options.
 * @param {File} file - The image file to compress.
 * @param {Object} compressionOptions - The compression options.
 * @param {number} compressionOptions.maxFilesize - The maximum file size in megabytes.
 * @param {boolean} compressionOptions.preserveExif - Whether to preserve EXIF data.
 * @param {number} compressionOptions.maxWidthOrHeight - The maximum width or height of the compressed image.
 * @returns {Promise<File>} A promise that resolves to the compressed image file.
 */
export async function compressImage(file, compressionOptions = {}) {
  if (!file.type.startsWith('image/')) {
    console.error('The provided file is not an image.');
    return;
  }

  const options = {
    maxSizeMB: compressionOptions.maxFilesize || file.size > 20 ? 10 : 1,
    preserveExif: compressionOptions.preserveExif || true,
    maxWidthOrHeight: compressionOptions.maxWidthOrHeight || undefined,
    useWebWorker: true,
    alwaysKeepResolution: true,
    fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
  }

  const compressedBlob = await imageCompression(file, options);
  return new File([compressedBlob], file.name, { type: file.type });
}
